import { ContentSkeleton } from '@cp/ds/src/components/contentSkeleton';
import React, { useMemo } from 'react';
import { CatalogItemViewModel } from '@common/model/catalog/item/view';
import { CITY_PATH_PARAMETER, usePathGenerator } from '@cp/entities/clientWebRoutes';
import { DEFAULT_CATALOG_PER_PAGE } from '@common/const/catalog';
import Card from '@cp/ds/src/components/card';
import { coreCatalogApi } from '@cp/entities/catalog';
import { generateCatalogItemPermalink } from 'src/entities/catalog/item';
import { CatalogFlexList, CatalogListItem, EmptyCatalog } from 'src/entities/catalog';
import { RoutedLink } from 'src/shared/ui/link';
import { setFakeOnlineStatus } from '../model';
import { CatalogListLinksSchema } from './listLinksSchema';
import { CatalogListProps } from './catalogList';

const ITEM_SPACING_DEFAULT = 2.5;

const getKey = (data: CatalogItemViewModel) => {
  return [data.type, data.careerId, data.id].map(String).join('-');
};

const DefaultFallback = () => (
  <Card sx={{ backgroundColor: 'white', borderRadius: '24px', width: '100%' }}>
    <ContentSkeleton blocks={1} sx={{ margin: 1.5 }} />
  </Card>
);

export const CatalogListSimilar = ({
  sx,
  request,
  itemWidth,
  itemSpacing = ITEM_SPACING_DEFAULT,
  CatalogItem,
  FallbackCard = DefaultFallback,
  ListEntity = CatalogFlexList,
}: CatalogListProps) => {
  const { data, isLoading } = coreCatalogApi.endpoints.getItems.useQuery(request);
  const generatePath = usePathGenerator();
  const items = useMemo(() => {
    return isLoading ? new Array<null>(request.pagination || DEFAULT_CATALOG_PER_PAGE).fill(null) : setFakeOnlineStatus(data?.items ?? []);
  }, [data, isLoading, request.pagination]);

  const isEmpty = !items.length && !isLoading;

  return (
    <ListEntity
      sx={{
        gap: itemSpacing,
        ...sx,
      }}
    >
      {isEmpty ? (
        <EmptyCatalog />
      ) : (
        <>
          {items[0] !== null && <CatalogListLinksSchema items={items as CatalogItemViewModel[]} />}
          {items.map((item: CatalogItemViewModel | null, index: number) => {
            if (!item) {
              return (
                <CatalogListItem key={index} sx={{ width: itemWidth }}>
                  <FallbackCard />
                </CatalogListItem>
              );
            }

            const href = generateCatalogItemPermalink(item, generatePath, { CITY_PATH_PARAMETER });

            return (
              <CatalogListItem key={getKey(item)} sx={{ width: itemWidth }}>
                <RoutedLink disableHover href={href} sx={{ display: 'contents' }} underline={false}>
                  <CatalogItem data={item} index={index} />
                </RoutedLink>
              </CatalogListItem>
            );
          })}
        </>
      )}
    </ListEntity>
  );
};
